import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { Tooltip } from "antd";
import Icon from "@mdi/react";
import { mdiInformationOutline, mdiMinus, mdiPlus } from "@mdi/js";
import useBreakpoint from "../../../../utils/useBreakpoint";
import { currencyDisplay } from "../../../../utils/helpers";
import ScrollCarousel from "../../v2/components/ScrollCarousel";
import { LandTourDetail, LandTourCategory, LandTourPriceDate } from "../../../../interfaces/land-tour.interface";
import TravelDateModal from "./TravelDateModal";
import SurchargesModal from "./SurchargesModal";

const ICONS = "/iceholidays-assets/images/icons";

// Past this length the chip clamps to 3 lines and gets an info icon + tooltip for the full text.
const LONG_CATEGORY_LABEL_THRESHOLD = 50;

export interface PaxCounts {
  adult: number;
  single_supplement: number;
  child_with_bed: number;
  child_without_bed: number;
  child_twin: number;
  infant: number;
}

export const EMPTY_PAX_COUNTS: PaxCounts = {
  adult: 0,
  single_supplement: 0,
  child_with_bed: 0,
  child_without_bed: 0,
  child_twin: 0,
  infant: 0,
};

export const PAX_TYPES: { key: keyof PaxCounts; label: string }[] = [
  { key: "adult", label: "Adult" },
  { key: "single_supplement", label: "Adult (Single Room)" },
  { key: "child_with_bed", label: "Child with Extra bed" },
  { key: "child_without_bed", label: "Child without bed" },
  { key: "child_twin", label: "Child twin" },
  { key: "infant", label: "Infant" },
];

// Mobile lists the traveller rows in this order (Figma 10244:300049); desktop keeps PAX_TYPES's.
const MOBILE_PAX_ORDER: (keyof PaxCounts)[] = [
  "adult", "single_supplement", "child_twin", "child_with_bed", "child_without_bed", "infant",
];

const DEFAULT_CHILD_AGE_RANGE = { min_age: 2, max_age: 11 };

export const maxTravellers = (
  landTour: { maxBooking?: number },
  priceDate: { maxBooking?: number } | undefined
): number | undefined => {
  const caps = [landTour.maxBooking, priceDate?.maxBooking].filter((cap): cap is number => !!cap && cap > 0);
  return caps.length ? Math.min(...caps) : undefined;
};

const TRIM_ORDER: (keyof PaxCounts)[] = ["child_without_bed", "child_twin", "child_with_bed", "single_supplement", "adult"];

// The tour's minimum applies to the total headcount, not to any one traveller type; one adult always stays.
export const canDecreasePax = (counts: PaxCounts, key: keyof PaxCounts, minTravellers: number): boolean => {
  if (counts[key] <= 0) return false;
  const next = { ...counts, [key]: counts[key] - 1 };
  if (totalPax(next) < minTravellers) return false;
  const isAdultRow = key === "adult" || key === "single_supplement";
  return !isAdultRow || next.adult + next.single_supplement > 0;
};

// A single departure date plus the LandTourPriceDate window it belongs to. One
// LandTourPriceDate backs many individual dates (a departure window), so the
// window alone can't identify which date the user picked - see the comment on
// firstDateSelection below for why this used to be a bug.
export interface DateSelection {
  date: string;
  priceDate: LandTourPriceDate;
}

interface Props {
  landTour: LandTourDetail;
  selectedCategory: LandTourCategory | undefined;
  onCategoryChange: (category: LandTourCategory) => void;
  selectedDate: DateSelection | undefined;
  onDateChange: (selection: DateSelection) => void;
  paxCounts: PaxCounts;
  onPaxCountsChange: (counts: PaxCounts) => void;
}

export interface DateRules {
  bookingCutOffDay?: number;
  departDay?: string[];
}

export const isBookableDate = (date: string, rules: DateRules = {}): boolean => {
  const day = dayjs(date);
  // Today plus the cut-off days are closed (cut-off 3 on 25 Sep closes 25-28 Sep), so the first bookable day is the next one.
  const earliest = dayjs().add((rules.bookingCutOffDay || 0) + 1, "day");
  if (day.isBefore(earliest, "day")) return false;
  return !rules.departDay?.length || rules.departDay.includes(day.format("ddd"));
};

// The earliest bookable date for a category, used both as the initial selection
// on load and whenever the category switches. Scans price_dates directly rather
// than depending on this component's internal dateOptions memo, so the parent
// page can compute it too without lifting that memo.
export const firstDateSelection = (
  category: LandTourCategory | undefined,
  rules: DateRules = {}
): DateSelection | undefined => {
  if (!category) return undefined;
  let earliest: DateSelection | undefined;
  category.price_dates.forEach((priceDate) => {
    priceDate.date.forEach((date) => {
      if (!isBookableDate(date, rules)) return;
      if (!earliest || date < earliest.date) earliest = { date, priceDate };
    });
  });
  return earliest;
};

// The active pricing tier for a given price-date is picked by total headcount
// (adults + children; infants typically don't occupy a seat/bed), same idea as
// the series booking form's per-tier price lookup, but LandTour's quantities
// are already expanded arrays so no range-string parsing is needed.
export const totalPax = (counts: PaxCounts) =>
  counts.adult + counts.single_supplement + counts.child_with_bed + counts.child_without_bed + counts.child_twin;

export const pricingPaxCount = (counts: PaxCounts) =>
  Math.floor(
    counts.adult +
      counts.single_supplement +
      counts.child_with_bed +
      counts.child_without_bed * 0.5 +
      counts.child_twin
  );

// Tiers sorted by their lowest quantity, ascending - the jbuilder emits them in
// unsorted hash order (app/views/api/v1/land_tours/show.json.jbuilder), and
// LandTours::PriceQuote (iceholidays#4453) explicitly sorts by range start
// before picking a "first tier", so usePriceQuote's local fallback needs the
// same ordering to agree with the server. Exported so that fallback can reuse it.
export const sortedTiers = (priceDate: LandTourPriceDate | undefined) =>
  [...(priceDate?.quantities || [])].sort((a, b) => (a.quantity[0] ?? 0) - (b.quantity[0] ?? 0));

export const pricingTierFor = (priceDate: LandTourPriceDate | undefined, counts: PaxCounts) => {
  if (!priceDate) return undefined;
  const tiers = sortedTiers(priceDate);
  if (!tiers.length) return undefined;
  const pax = Math.max(pricingPaxCount(counts), 1);
  // Tiered pricing decreases monotonically as headcount grows, so falling back to
  // the nearest tier only makes sense on the low side (pax below every tier's
  // range, e.g. the traveller count dipped under min_booking mid-edit) - clamp to
  // the cheapest/first tier there. Falling through to the *last* tier here would
  // silently quote the group rate for an under-count instead of the entry rate.
  return (
    tiers.find((tier) => tier.quantity.includes(pax)) ||
    (pax < (tiers[0].quantity[0] ?? 0) ? tiers[0] : tiers[tiers.length - 1])
  );
};

export const priceForPaxType = (
  priceDate: LandTourPriceDate | undefined,
  counts: PaxCounts,
  type: keyof PaxCounts
): number => {
  if (type === "single_supplement") return priceDate?.singleSupplementPrice || 0;
  const tier = pricingTierFor(priceDate, counts);
  if (!tier) return 0;
  if (type === "adult") return tier.adult;
  return tier.child_prices.find((cp) => cp.type === type)?.price || 0;
};

export default function ChooseYourTour({
  landTour,
  selectedCategory,
  onCategoryChange,
  selectedDate,
  onDateChange,
  paxCounts,
  onPaxCountsChange,
}: Props) {
  const isMobile = useBreakpoint();
  const [showAllDates, setShowAllDates] = useState(false);
  const [showSurcharges, setShowSurcharges] = useState(false);
  const selectedChipRef = useRef<HTMLButtonElement | null>(null);

  const dateRules = useMemo(
    () => ({ bookingCutOffDay: landTour.bookingCutOffDay, departDay: landTour.departDay }),
    [landTour.bookingCutOffDay, landTour.departDay]
  );

  const dateOptions = useMemo(() => {
    if (!selectedCategory) return [];
    const seen = new Set<string>();
    const options: { date: string; priceDate: LandTourPriceDate }[] = [];

    selectedCategory.price_dates.forEach((priceDate) => {
      priceDate.date.forEach((date) => {
        if (seen.has(date) || !isBookableDate(date, dateRules)) return;
        seen.add(date);
        options.push({ date, priceDate });
      });
    });

    return options.sort((a, b) => a.date.localeCompare(b.date));
  }, [selectedCategory, dateRules]);

  useEffect(() => {
    const chip = selectedChipRef.current;
    const track = chip?.closest<HTMLElement>(".v2-carousel__track");
    if (!chip || !track) return;

    const chipBox = chip.getBoundingClientRect();
    const trackBox = track.getBoundingClientRect();
    const centred = track.scrollLeft + (chipBox.left - trackBox.left) - (trackBox.width - chipBox.width) / 2;
    track.scrollTo({ left: Math.max(centred, 0), behavior: "smooth" });
  }, [selectedDate?.date]);

  // A surcharge with no categories listed applies tour-wide; otherwise it only applies to the
  // categories it names. Previously every surcharge was shown regardless of the selected
  // category (issue #629 (b)6 asks for this scoped to the category).
  const categorySurcharges = useMemo(
    () =>
      landTour.surcharges.filter(
        (surcharge) =>
          !surcharge.categories?.length || surcharge.categories.some((category) => category.id === selectedCategory?.id)
      ),
    [landTour.surcharges, selectedCategory]
  );

  const travellerCap = maxTravellers(landTour, selectedDate?.priceDate);
  const minTravellers = Math.max(landTour.minBooking || 1, 1);

  const adultFromAge =
    Math.max(
      DEFAULT_CHILD_AGE_RANGE.max_age,
      ...Object.values(landTour.childAgeRanges || {}).map((range) => range?.max_age ?? 0)
    ) + 1;

  useEffect(() => {
    if (!travellerCap || totalPax(paxCounts) <= travellerCap) return;

    const trimmed = { ...paxCounts };
    for (const key of TRIM_ORDER) {
      while (totalPax(trimmed) > travellerCap && canDecreasePax(trimmed, key, minTravellers)) trimmed[key] -= 1;
    }
    onPaxCountsChange(trimmed);
  }, [travellerCap]);

  const setPaxCount = (key: keyof PaxCounts, delta: number) => {
    if (delta < 0 && !canDecreasePax(paxCounts, key, minTravellers)) return;
    const nextCounts = { ...paxCounts, [key]: Math.max(0, paxCounts[key] + delta) };

    if (delta > 0 && travellerCap && totalPax(nextCounts) > travellerCap) return;

    onPaxCountsChange(nextCounts);
  };

  if (!landTour.pricingCategories.length) return null;

  return (
    <section className="ground-tour-show-v2__choose">
      <h2 className="ground-tour-show-v2__section-title">Tour Category</h2>

      <div className="ground-tour-show-v2__category-list">
        {landTour.pricingCategories.map((category) => {
          const isLong = category.category.length > LONG_CATEGORY_LABEL_THRESHOLD;

          return (
            <button
              key={category.id}
              type="button"
              className={`ground-tour-show-v2__category-chip ${selectedCategory?.id === category.id ? "is-selected" : ""}`}
              onClick={() => onCategoryChange(category)}
            >
              <span className="ground-tour-show-v2__category-chip-label">{category.category}</span>

              {isLong && (
                <Tooltip title={category.category}>
                  <span className="ground-tour-show-v2__category-chip-info">
                    <Icon path={mdiInformationOutline} size="16px" />
                  </span>
                </Tooltip>
              )}
            </button>
          );
        })}
      </div>

      <div className="ground-tour-show-v2__date-picker">
        <div className="ground-tour-show-v2__date-picker-header">
          <h3>Select Date</h3>

        </div>

        <div className="ground-tour-show-v2__date-strip">
          <button
            type="button"
            className="ground-tour-show-v2__view-all-dates"
            onClick={() => setShowAllDates(true)}
            aria-label="View all dates"
          >
            <img src={`${ICONS}/icon-calendar-red.svg`} alt="" aria-hidden="true" />
            {!isMobile && "View All Dates"}
          </button>

          <ScrollCarousel trackClassName="ground-tour-show-v2__date-track" step={100}>
            {dateOptions.map(({ date, priceDate }) => {
              const parsed = dayjs(date);
              const isSelected = selectedDate?.date === date;

              return (
                <button
                  key={date}
                  type="button"
                  ref={isSelected ? selectedChipRef : undefined}
                  className={`ground-tour-show-v2__date-chip ${isSelected ? "is-selected" : ""}`}
                  onClick={() => onDateChange({ date, priceDate })}
                >
                  <span className="ground-tour-show-v2__date-year">{parsed.format("YYYY")}</span>
                  <span className="ground-tour-show-v2__date-day">{parsed.format("DD MMM")}</span>
                </button>
              );
            })}
          </ScrollCarousel>
        </div>
      </div>

      <div className="ground-tour-show-v2__travellers">
        <h3>
          Travellers{" "}
          {landTour.minBooking ? (
            <span className="ground-tour-show-v2__travellers-min">(Min. {landTour.minBooking} Pax)</span>
          ) : null}
        </h3>

        <div className="ground-tour-show-v2__pax-grid">
          {(isMobile
            ? MOBILE_PAX_ORDER.map((key) => PAX_TYPES.find((type) => type.key === key)!)
            : PAX_TYPES
          ).map(({ key, label }) => {
            const price = priceForPaxType(selectedDate?.priceDate, paxCounts, key);
            // Already live (BE #4358); previously unmapped, so child pax cards showed price
            // only with no age copy (issue #629 (b)6).
            const configuredAge = landTour.childAgeRanges?.[key];
            const ageRange =
              configuredAge?.min_age != null && configuredAge?.max_age != null
                ? configuredAge
                : key.startsWith("child") ? DEFAULT_CHILD_AGE_RANGE : undefined;

            // Mirrors setPaxCount's own bounds so the stepper buttons reflect what a press would do.
            const atFloor = !canDecreasePax(paxCounts, key, minTravellers);
            const atCeiling = !!travellerCap && totalPax(paxCounts) + 1 > travellerCap;

            const ageCopy = isMobile
              ? key === "adult" || key === "single_supplement"
                ? `${adultFromAge} years & above`
                : ageRange ? `${ageRange.min_age}-${ageRange.max_age} years old` : null
              : ageRange ? `${ageRange.min_age}-${ageRange.max_age} yrs` : null;

            const meta = [ageCopy, price > 0 ? currencyDisplay("MYR", price) : null].filter(Boolean).join(" • ");

            return (
              <div className={isMobile ? "ground-tour-show-v2__pax-row" : "ground-tour-show-v2__pax-card"} key={key}>
                <div className="ground-tour-show-v2__pax-info">
                  <span className="ground-tour-show-v2__pax-label">{label}</span>
                  {meta && <span className="ground-tour-show-v2__pax-meta">{meta}</span>}
                </div>

                <div className="ground-tour-show-v2__pax-stepper">
                  <button
                    type="button"
                    onClick={() => setPaxCount(key, -1)}
                    disabled={atFloor}
                    aria-label={`Decrease ${label}`}
                  >
                    {isMobile ? (
                      <img src={`${ICONS}/${atFloor ? "icon-step-minus-off" : "icon-step-minus"}.svg`} alt="" aria-hidden="true" />
                    ) : (
                      <Icon path={mdiMinus} size="16px" />
                    )}
                  </button>
                  <span>{paxCounts[key]}</span>
                  <button
                    type="button"
                    onClick={() => setPaxCount(key, 1)}
                    disabled={atCeiling}
                    aria-label={`Increase ${label}`}
                  >
                    {isMobile ? (
                      <img src={`${ICONS}/${atCeiling ? "icon-step-plus-off" : "icon-step-plus"}.svg`} alt="" aria-hidden="true" />
                    ) : (
                      <Icon path={mdiPlus} size="16px" />
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {categorySurcharges.length > 0 && (
          <div className="ground-tour-show-v2__surcharge-row">
            <span>Surcharges applied</span>
            <button type="button" onClick={() => setShowSurcharges(true)}>
              View details
              <img src={`${ICONS}/icon-open-arrow.png`} alt="" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>

      <TravelDateModal
        open={showAllDates}
        onClose={() => setShowAllDates(false)}
        options={dateOptions}
        selectedDate={selectedDate?.date}
        onConfirm={onDateChange}
      />

      <SurchargesModal
        open={showSurcharges}
        onClose={() => setShowSurcharges(false)}
        surcharges={categorySurcharges}
        categoryName={selectedCategory?.category}
      />
    </section>
  );
}
